import { WebRTC } from "./webrtc";
import { ActionMessage, WebRTCChannel } from "./channel";
import { WebRTCBase } from "./base";
import { SequenceWaiter } from "../common/sequence_waiter";
import { arrayBufferLikeCopy } from "../common/util";
import { DownloaderWebrtcAction, } from "../common/downloader";
import { AppError } from "../common/app_error";
import { SpeakableWebRTC } from "./speakable_webrtc";
import { EventWebrtcAction, PresetWebrtcAction } from "../common/base";
export var ExtraDataWebrtcLabel;
(function (ExtraDataWebrtcLabel) {
    ExtraDataWebrtcLabel["Download"] = "downloader.download";
    ExtraDataWebrtcLabel["Preset"] = "ptz.preset.get_img";
    ExtraDataWebrtcLabel["Event"] = "event.get_media";
})(ExtraDataWebrtcLabel || (ExtraDataWebrtcLabel = {}));
export var ImgTag;
(function (ImgTag) {
    ImgTag[ImgTag["SNAPSHOT"] = 0] = "SNAPSHOT";
    ImgTag[ImgTag["PRESET"] = 1] = "PRESET";
})(ImgTag || (ImgTag = {}));
export class DataWebRTC extends SpeakableWebRTC {
    constructor(params, settings) {
        super(params, settings);
        this.waiter = new SequenceWaiter("DataWebRTC");
        this.unconnectedLabels = Object.values(ExtraDataWebrtcLabel);
        this.extralChannel = new Map();
        this.addEventListener(DataWebRTC.CHANNEL_DEVICE_SNAPSHOTS, (channel, channelObject) => {
            this.onDeviceSnapshot(channelObject);
        });
        DataWebRTC.EXTRA_LABELS.forEach((label) => {
            this.extralChannel.set(label, this.createExtraChannel(label));
        });
    }
    createExtraChannel(label) {
        if (this.extralChannel.has(label)) {
            return;
        }
        const channel = this.peerConn.createDataChannel(label);
        channel.onerror = (e) => {
            console.error("error in channel: connId=%s, error=%s, label=%s", this.connId, e, label);
        };
        const self = this;
        const dataChannel = new WebRTCChannel({
            connId: this.connId,
            channel: channel,
            onObject: this.onExtraAction.bind(this),
            onClose: this.onExtraClose.bind(this),
            onOpen: (channel) => {
                if (this.debugging)
                    console.debug("download channel opened: connId=%s, label=%s", this.connId, label);
                self.emitEvent(label + WebRTC.CHAN_OPEN_SUFFIX, channel);
                const index = this.unconnectedLabels?.findIndex(channel => label === channel);
                if (index >= 0) {
                    this.unconnectedLabels?.splice(index, 1);
                }
                if (this.unconnectedLabels?.length === 0) {
                    self.emitEvent(DataWebRTC.EXTRA_CHANNEL_CONNECTED);
                }
            },
        });
        return dataChannel;
    }
    onExtraClose(channel, channelObject) {
        this.extralChannel.delete(channel.label);
        this.emitEvent(WebRTC.CHAN_CLOSE_PREFIX + channel.label, channel);
    }
    onExtraAction(channel, channelObject) {
        this.onActionMessage(channel, channelObject);
    }
    doOnDeviceSnapshot(error, meta, obj) {
        const snapshot = {
            properties: meta.snapshotRes,
            imageBitmap: obj instanceof ImageBitmap ? obj : null,
            imageBlob: obj instanceof Blob ? obj : null,
        };
        const deviceSnapshot = {
            error,
            actId: meta.actId,
            camera: meta.snapshotRes.camera,
            snapshot,
        };
        // should or not?
        delete meta.snapshotRes.camera;
        this.emitEvent(DataWebRTC.EVENT_DEVICE_SNAPSHOTS, deviceSnapshot);
    }
    doOnCameraPresetSnapshot(error, meta, obj) {
        if (error) {
            console.error("presetGetImg: error=%s", error);
            meta?.callback?.(error, null);
            return;
        }
        const snapshot = {
            properties: meta.snapshotRes,
            imageBitmap: obj instanceof ImageBitmap ? obj : null,
        };
        const cameraPreset = {
            error,
            id: meta.id,
            actId: meta.actId,
            camera: meta?.snapshotRes?.camera,
            snapshot,
        };
        const actionMessage = new ActionMessage(meta.actId, PresetWebrtcAction.GET_IMG, cameraPreset);
        meta?.callback?.(null, actionMessage);
    }
    onDeviceSnapshot(channelObject) {
        const meta = channelObject.json;
        meta.tag = ImgTag.SNAPSHOT;
        const snapshotRes = meta.snapshotRes;
        if (snapshotRes.sent_at)
            snapshotRes.sent_duration = (new Date().getTime() - snapshotRes.sent_at) / 1000;
        if (this.debugging)
            console.debug("device_snapshot: meta=%s", JSON.stringify(meta));
        if (meta.error) {
            this.doOnDeviceSnapshot(AppError.fromObject(meta.error), meta, null);
            return;
        }
        const format = channelObject.json["snapshotRes"].format;
        if (format === "image/frame") {
            this.sendDecodeImage(meta, channelObject.binary);
        }
        else {
            const blob = new Blob([channelObject.binary], { type: "image/" + format });
            this.doOnDeviceSnapshot(null, meta, blob);
        }
    }
    sendDecodeImage(meta, data) {
        const worker = this.getWorker(WebRTC.LEGACY_WORKER_ID);
        const imageFrame = { data: arrayBufferLikeCopy(data), sequence: this.waiter.sequence };
        const args = { imageFrame, debugging: this.debugging };
        worker.postMessage({ type: WebRTCBase.ACTION_DECODE_IMAGE, args: args }, [imageFrame.data]);
        this.waiter.setAction({ meta });
    }
    onDecodeImage(args) {
        const action = this.waiter.unsetAction(args.sequence);
        if (action) {
            let error = null;
            if (args.error) {
                error = AppError.fromError(args.error);
            }
            if (action.meta.tag === ImgTag.SNAPSHOT) {
                this.doOnDeviceSnapshot(error, action.meta, args.imageBitmap);
            }
            else {
                this.doOnCameraPresetSnapshot(error, action.meta, args.imageBitmap);
            }
        }
    }
    onMessage(data) {
        if (super.onMessage(data))
            return true;
        switch (data.type) {
            case WebRTCBase.ACTION_DECODE_IMAGE_REPLY:
                this.onDecodeImage(data.args);
                return true;
        }
        return false;
    }
    downloadVideo(param, callback, timeout, label = ExtraDataWebrtcLabel.Download) {
        return this.sendActionMessage(DownloaderWebrtcAction.Download, param, callback, timeout, this.extralChannel.get(label));
    }
    downloaderList(param, callback, timeout, label = ExtraDataWebrtcLabel.Download) {
        return this.sendActionMessage(DownloaderWebrtcAction.List, param, callback, timeout, this.extralChannel.get(label));
    }
    getMediaImg(param, callback, timeout, label = ExtraDataWebrtcLabel.Event) {
        return this.sendActionMessage(EventWebrtcAction.GetMedia, param, callback, timeout, this.extralChannel.get(label));
    }
    recordDaily(param, callback, timeout, label = ExtraDataWebrtcLabel.Download) {
        return this.sendActionMessage(DownloaderWebrtcAction.RecordDaily, param, callback, timeout, this.extralChannel.get(label));
    }
    abortVideo(param, callback) {
        return this.sendActionMessage(DownloaderWebrtcAction.Abort, param, callback);
    }
    completeVideo(param, callback) {
        return this.sendActionMessage(DownloaderWebrtcAction.Complete, param, callback);
    }
    presetList(param, callback, timeout, label = ExtraDataWebrtcLabel.Preset) {
        return this.sendActionMessage(PresetWebrtcAction.LIST, param, callback, timeout);
    }
    presetSet(param, callback, timeout, label = ExtraDataWebrtcLabel.Preset) {
        return this.sendActionMessage(PresetWebrtcAction.SET, param, callback, timeout);
    }
    presetDelete(param, callback, timeout, label = ExtraDataWebrtcLabel.Preset) {
        return this.sendActionMessage(PresetWebrtcAction.DELETE, param, callback, timeout);
    }
    presetGoto(param, callback, timeout, label = ExtraDataWebrtcLabel.Preset) {
        return this.sendActionMessage(PresetWebrtcAction.GOTO, param, callback, timeout);
    }
    presetGetImg(param, callback, timeout, label = ExtraDataWebrtcLabel.Preset) {
        console.log('presetGetImg: param=%s', JSON.stringify(param));
        const cbFun = (error, result) => {
            if (error) {
                console.error("presetGetImg: error=%s", error);
                callback?.(error, result);
                return;
            }
            const meta = {
                actId: result.id,
                error: result.error,
                id: result.data.id,
                snapshotRes: {
                    camera: result?.data?.camera,
                    width: 0,
                    height: 0,
                    format: 'string',
                    size: 0,
                    sent_at: 0,
                    sent_duration: 0,
                },
                callback: callback,
                tag: ImgTag.PRESET,
            };
            this.sendDecodeImage(meta, result.obj.binary);
        };
        return this.sendActionMessage(PresetWebrtcAction.GET_IMG, param, cbFun, timeout, this.extralChannel.get(label));
    }
}
DataWebRTC.CHANNEL_DEVICE_SNAPSHOTS = WebRTC.CHAN_MSG_PREFIX + "device.take_snapshots";
DataWebRTC.EVENT_ACTION_DOWNLOAD = WebRTC.CHAN_MSG_PREFIX + DownloaderWebrtcAction.Download;
DataWebRTC.EVENT_DEVICE_SNAPSHOTS = "device_snapshots";
DataWebRTC.EVENT_DOWNLOAD_OPEN = ExtraDataWebrtcLabel.Download + WebRTC.CHAN_OPEN_SUFFIX;
DataWebRTC.EXTRA_CHANNEL_CONNECTED = "extra_channel_connected";
DataWebRTC.EXTRA_LABELS = Object.values(ExtraDataWebrtcLabel);
